import { FC, forwardRef } from 'react';
import { DialogProps } from '@toolpad/core/useDialogs';
import {
  Dialog,
  Slide,
  Box,
  DialogContent,
  Stack,
  Typography,
  Button,
  useMediaQuery,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { CheckboxCheckedIcon } from '@ui/icons';
import { ViewNavigationBar } from '@components';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="up"
      easing={{
        enter: 'cubic-bezier(0.66, 0, 0.34, 1)',
        exit: 'linear',
      }}
      ref={ref}
      {...props}
    />
  );
});

export const LanguageSelectDialog: FC<DialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();

  const nativeLanguageNames: { [key: string]: string } = {
    en: 'English',
    de: 'Deutsch',
    es: 'Español',
    fr: 'Français',
    it: 'Italiano',
    // add more as needed
  };

  const availableLanguages = Object.keys(i18n.services.resourceStore.data);

  const handleChangeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    // Persist the explicit choice so it wins over the browser language on
    // future visits (see the detection policy in i18n.ts). The detector
    // itself deliberately does not cache auto-detected languages.
    try {
      localStorage.setItem('userLanguage', language);
    } catch {
      // localStorage unavailable (e.g. private mode) — choice applies to
      // this session only.
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        fullWidth={true}
        maxWidth="md"
        fullScreen={fullScreen}
        scroll="paper"
        slots={{
          transition: Transition,
        }}
        slotProps={{
          paper: {
            sx: {},
          },
        }}
      >
        <ViewNavigationBar
          onClose={() => onClose()}
          title={t('languageSelectDialogTitle')}
        />
        <DialogContent
          sx={{
            position: 'relative',
            height: '100%',
            minHeight: {
              md: 300,
            },
            padding: 6,
            backgroundColor: '#FFFFFF',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexGrow: 0,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Stack direction="column" spacing={4} sx={{ width: '100%' }}>
              {availableLanguages.map((language) => {
                const isActive = i18n.language === language;

                return (
                  <Box
                    key={language}
                    onClick={() => {
                      if (!isActive) {
                        handleChangeLanguage(language);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#ECF1FC',
                      paddingTop: 4,
                      paddingBottom: 4,
                      paddingLeft: 8,
                      paddingRight: 8,
                      borderRadius: 4,
                      cursor: isActive ? 'default' : 'pointer',
                    }}
                  >
                    <Stack direction={'row'} spacing={6} alignItems={'center'}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: 50,
                          border: isActive ? 'none' : '2px solid #0032A0',
                        }}
                      >
                        {isActive && <CheckboxCheckedIcon />}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: '#0032A0',
                          }}
                        >
                          {nativeLanguageNames[language]}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: '#0032A0',
                          }}
                        >
                          {t(`language${language.toUpperCase()}`)}
                        </Typography>
                      </Box>
                    </Stack>
                    {isActive ? (
                      <Button variant="contained" disabled>
                        Aktiv
                      </Button>
                    ) : (
                      <Button variant="contained">Auswählen</Button>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
